import { API_BASE } from '@/utils/apiBase';

/**
 * Rewrite browser fetches for `/api...` so a separately-hosted API can sit on
 * a different origin without forcing a repo-wide component rewrite.
 *
 * When `REACT_APP_API_BASE_URL` is unset this is effectively a no-op.
 */
export function installApiBaseFetchShim(): void {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  if (!API_BASE || API_BASE === window.location.origin) return;

  const globalWindow = window as typeof window & {
    __tsApiBaseFetchShimInstalled?: boolean;
  };
  if (globalWindow.__tsApiBaseFetchShimInstalled) return;
  globalWindow.__tsApiBaseFetchShimInstalled = true;

  const originalFetch = window.fetch.bind(window);
  const sameOriginPrefix = `${window.location.origin}/api`;
  const apiBasePrefix = `${API_BASE}/api`;

  const rewriteUrl = (raw: string): string => {
    if (raw.startsWith("/api")) return `${API_BASE}${raw}`;
    if (raw.startsWith(sameOriginPrefix)) return `${apiBasePrefix}${raw.slice(sameOriginPrefix.length)}`;
    return raw;
  };

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === "string") {
      return originalFetch(rewriteUrl(input), init);
    }

    if (input instanceof URL) {
      return originalFetch(new URL(rewriteUrl(input.toString())), init);
    }

    if (typeof Request !== "undefined" && input instanceof Request) {
      const rewrittenUrl = rewriteUrl(input.url);
      if (rewrittenUrl === input.url) return originalFetch(input, init);
      const rewrittenRequest = new Request(rewrittenUrl, input);
      return originalFetch(rewrittenRequest, init);
    }

    return originalFetch(input, init);
  }) as typeof window.fetch;
}
